<template>
	<div v-if="entity" class="upgrade-settlement">
		<div v-if="entity.isQuestCompleted" class="available-upgrade">
			<span class="available-upgrade__heading">
				{{ translate("ui.price") }}:
			</span>
			<ResourceGroupContainer
				class="available-upgrade__resources"
				:resources="upgradeResources"
				:requiredFood="requiredFood"
				:hasLowStorage="hasLowStorage"
				:upgradeMainSettlement="upgradeMainSettlement"
			/>

			<AppTextButton
				v-if="!upgradeMainSettlement"
				class="available-upgrade__button"
				:type="StatusTypes.SUCCESS"
				:disabled="!entity.canLevelUp"
				:text="translate('ui.upgrade')"
				@click="levelUp"
			/>
		</div>
		<div
			v-else-if="hasMaxLevel"
			:class="{ 'switch-showed': showArrowsToSettlementSwitch }"
		>
			<div class="upgrade-settlement__description--l">
				{{
					entity.isMainSettlement
						? translate("ui.town_maxed")
						: translate("ui.lesser_town_maxed")
				}}
			</div>
		</div>
		<div class="upgrade-settlement__requirements" v-else>
			<span class="upgrade-settlement__heading">
				{{ translate("ui.upgrade_requirements") }}

				<Tooltip :placement="Placements.BOTTOM" v-if="entity.questHint">
					<template #element>
						<AppIconButton
							:type="IconTypes.INFO"
							:size="12"
							class="quest-task__hint-icon"
						/>
					</template>
					<template #tooltip>
						<span>{{ entity.questHint }}</span>
					</template>
				</Tooltip>
			</span>
			<span
				class="upgrade-settlement__description"
				v-html="entity.questDescriptionHtml"
			/>
			<span
				class="upgrade-settlement__progress"
				v-html="entity.questProgressHtml"
			/>
			<AppTextButton
				v-if="!entity.isMainSettlement && !!entity.skipQuestGemsPrice"
				class="upgrade-settlement__button"
				:type="StatusTypes.SECONDARY"
				:text="translate('button.skip')"
				@click="openSkipPopup"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import ResourceGroupContainer from "@/components/ResourceGroupContainer.vue";
import SkipRequirementsPopup from "@/components/Settlement/SkipRequirementsPopup.vue";
import Tooltip from "@/components/Tooltip.vue";
import SettlementCastleBuildingTileEntity from "@/game/entities/buildings/SettlementCastleBuildingTileEntity";
import { FrameName } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import {
	getSettlementConfigByLevel,
	MAX_SETTLEMENT_LEVEL,
} from "@/game/entities/config/settlements";
import SoundService from "@/game/SoundService";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useTileRepository from "@/hooks/useTileRepository";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStorageStore from "@/store/useResourcesStorageStore";
import useResourcesStore from "@/store/useResourcesStore";
import useSettingsStore from "@/store/useSettingsStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import { type TileIndex, type TileNeighbour } from "@/types/tile";
import { IconTypes, Placements, StatusTypes } from "@/types/ui";
import { formatPrice } from "@/utils/formatPrice";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, watch } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

const emitter = markRaw(useEmitter());
const tilerepo = markRaw(useTileRepository());
const soundService = markRaw(SoundService.getInstance());

const settlementStore = useSettlementsStore();
const resourcesStorageStore = useResourcesStorageStore();
const resourcesStorage = storeToRefs(resourcesStorageStore);
const resourcesStore = useResourcesStore();
const resources = storeToRefs(resourcesStore);

const settingsStore = useSettingsStore();
const { showArrowsToSettlementSwitch } = storeToRefs(settingsStore);

const popupStore = usePopupStore();

const emit = defineEmits(["isAvailableUpgrade"]);

let entity!: SettlementCastleBuildingTileEntity;

const resetEntity = (): void => {
	entity = markRaw(
		tilerepo.getTileEntityByTileCoordinates({
			x: +route.params.x,
			y: +route.params.y,
		}) as SettlementCastleBuildingTileEntity,
	);
	entity.initSettlementQuestCache();
};

resetEntity();

const upgradeMainSettlement = computed((): boolean => {
	if (!entity) return false;
	if (!entity.isMainSettlement && entity.level >= settlementStore.mainLevel) {
		return true;
	}
	return false;
});

const requiredFood = computed((): number => {
	if (!entity) return 0;
	if (!entity.consumption.has(ResourceType.FOOD_CONSUMPTION)) return 0;
	if (entity.level >= MAX_SETTLEMENT_LEVEL) return 0;

	const nextLevel = getSettlementConfigByLevel(entity.level + 1);
	if (!nextLevel) return 0;
	if (!nextLevel.consumption.has(ResourceType.FOOD_CONSUMPTION)) return 0;

	return (
		nextLevel.consumption.get(ResourceType.FOOD_CONSUMPTION)! -
		entity.consumption.get(ResourceType.FOOD_CONSUMPTION)!
	);
});

const hasMaxLevel = computed(
	(): boolean =>
		entity!.level >= MAX_SETTLEMENT_LEVEL ||
		!entity!.upgrades.has(SettlementCastleBuildingTileEntity.frameName) ||
		(!entity!.isMainSettlement &&
			entity!.level >= settlementStore.mainLevel),
);

const upgradeResources = computed((): Partial<Record<ResourceType, number>> => {
	const { price } = entity!.upgrades.get(
		SettlementCastleBuildingTileEntity.frameName,
	)!;
	return formatPrice(entity.resolveDynamicResources(price || {}));
});

const hasLowStorage = computed((): boolean => {
	return !!Object.entries(upgradeResources.value).find(
		([key, value]: [string, number]): boolean => {
			const storageState =
				resourcesStorage[key as keyof typeof resourcesStorage]?.value;
			const resourceState =
				resources[key as keyof typeof resources]?.value;
			return value > resourceState && value > storageState;
		},
	);
});

const levelUp = (): void => {
	if (!entity) return;
	if (!entity.canLevelUp) return;
	if (hasMaxLevel.value) return;

	entity.levelUp();

	if (entity.isMainSettlement) {
		emitter.emit(EmitterEvents.ToggleLevelUpPopup, true);
	}

	// update castle graphics
	emitter.emit(EmitterEvents.TileRepositoryUpgrade, {
		coordinates: { x: entity.x, y: entity.y },
		frameName: FrameName.SETTLEMENT_CASTLE,
		noRefresh: true,
	});
	emitter.emit(EmitterEvents.PhaserUpgradeAnimation, {
		x: entity.x,
		y: entity.y,
	});

	soundService.playButtonUpgrade();

	// update palisades
	const neighbours = tilerepo
		.getNeighbourTileIndexesByTileCoordinates({
			x: entity.x,
			y: entity.y,
		})
		.map((tileIndex: TileIndex): TileNeighbour | null => {
			const [, tileId1, , tileId3] =
				tilerepo.getTileIdsByTileIndex(tileIndex);
			const tileId = tileId1 || tileId3;
			if (!tileId) return null;

			const frameName = tilerepo.getFrameNameByTileId(tileId);
			if (!frameName) return null;

			return {
				tileIndex,
				tileId,
				frameName,
			};
		})
		.filter(
			(neighbour: TileNeighbour | null): boolean => neighbour !== null,
		) as Array<TileNeighbour>;

	neighbours.forEach((neighbour: TileNeighbour): void =>
		emitter.emit(EmitterEvents.TileRepositoryUpgrade, {
			coordinates: tilerepo.getTileCoordinatesByTileIndex(
				neighbour.tileIndex,
			),
			frameName: neighbour.frameName,
			noRefresh: true,
			onlyPalisades: true,
		}),
	);

	resetEntity();
};

const useSkip = (): void => {
	if (!entity) return;
	if (!entity.canSkipQuest) return;
	if (hasMaxLevel.value) return;
	if (entity.isMainSettlement) return;

	entity.skipQuest();
};

const openSkipPopup = (): void => {
	if (!entity.skipQuestGemsPrice) return;

	popupStore.showPopup({
		id: PopupDictionary.SKIP_REQUIREMENTS,
		type: PopupType.INFO,
		heading: translate("ui.skip_requirements"),
		height: 12,
		isCentered: true,
		isPriority: true,
		component: markRaw(SkipRequirementsPopup),
		componentProps: {
			entity: entity,
			onSkip: useSkip,
		},
	});
};

watch(
	(): boolean[] => [
		entity?.canLevelUp ?? false,
		entity?.isQuestCompleted ?? false,
		hasMaxLevel.value,
	],
	([canLevelUp, isQuestCompleted, hasMaxLevel]): void => {
		emit(
			"isAvailableUpgrade",
			canLevelUp && isQuestCompleted && !hasMaxLevel,
		);
	},
	{ immediate: true },
);

watch(
	(): number | undefined => resources[ResourceType.TICKS_TOTAL]?.value,
	(): void => {
		resetEntity();
	},
);
</script>

<style lang="scss" scoped>
.upgrade-settlement {
	@include flex-col-center;

	&__requirements {
		@include flex-col-center;
		margin: 0.5rem 0 0.375rem;
	}

	&__heading {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 0.8rem;
		margin-bottom: 0.75rem;
		color: #f2dcb5;
		font-weight: 800;
		font-size: 1rem;
		line-height: 1rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
	}

	&__description {
		margin-bottom: 0.624rem;
		color: #f2dcb5;
		font-size: 0.875rem;
		line-height: 1rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

		&--l {
			margin: 1rem 0;
			font-size: 1rem;
			line-height: 1rem;
		}
	}

	&__progress {
		margin-bottom: 0.375rem;
		color: #f2dcb5;
		font-size: 1.25rem;
		line-height: 1.25rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

		b {
			font-weight: 800;
		}
	}

	&__button {
		margin-top: 0.5rem;
	}

	.available-upgrade {
		@include flex-col-center;

		&__heading {
			margin-top: 0.625rem;
			margin-bottom: 0.75rem;
			color: #f2dcb5;
			font-weight: 800;
			font-size: 1rem;
			line-height: 1rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}

		&__resources {
			margin-bottom: 0.75rem;
		}

		&__button {
			margin-bottom: 0.375rem;
		}
	}

	.switch-showed {
		width: 70%;
		& > div {
			text-align: center;
		}
	}
}
</style>
